import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCheck, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/ui";
import { fromNow } from "@/lib/format";
import type { Notification } from "@shared/types";

const ICONS: Record<string, string> = {
  "task.assigned": "📋", "activity.status": "🔄", "evidence.added": "📎",
  "approval.requested": "✅", "approval.decided": "⚖️", "deadline.soon": "⏰",
  "deadline.overdue": "🔴", "project.added": "📁", system: "🔔",
};

export function NotificationsPage() {
  const qc = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "full", unreadOnly],
    queryFn: () => api.get<{ notifications: Notification[]; unreadCount: number }>(`/notifications?limit=100${unreadOnly ? "&unread=1" : ""}`),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const markRead = useMutation({ mutationFn: (b: any) => api.post("/notifications/read", b), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.del(`/notifications/${id}`), onSuccess: invalidate });

  return (
    <>
      <PageHeader title="Notifications" subtitle={`${data?.unreadCount ?? 0} unread`}
        actions={<>
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} /> Unread only</label>
          <button className="btn-outline text-xs" onClick={() => markRead.mutate({ all: true })}><CheckCheck size={14} /> Mark all read</button>
        </>}
      />
      {isLoading ? <PageLoader /> : !data?.notifications.length ? (
        <EmptyState title="No notifications" hint="You're all caught up." />
      ) : (
        <div className="card divide-y divide-line p-0">
          {data.notifications.map((n) => (
            <div key={n.id} className={`flex items-start gap-3 px-4 py-3 ${n.isRead ? "opacity-60" : ""}`}>
              <span className="text-xl leading-none">{ICONS[n.type] || "🔔"}</span>
              <Link to={n.link || "#"} className="min-w-0 flex-1" onClick={() => !n.isRead && markRead.mutate({ ids: [n.id] })}>
                <p className="text-sm font-medium">{n.title}</p>
                {n.body && <p className="text-xs text-ink-soft">{n.body}</p>}
                <p className="mt-0.5 text-[11px] text-ink-faint">{fromNow(n.createdAt)}{n.actorName ? ` · ${n.actorName}` : ""}</p>
              </Link>
              {!n.isRead && <button className="btn-ghost !p-1 text-xs" onClick={() => markRead.mutate({ ids: [n.id] })}>Mark read</button>}
              <button className="btn-ghost !p-1 text-ink-faint hover:text-st-delayed" onClick={() => remove.mutate(n.id)}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
